// SourceAttribution — ad-platform attribution panel for a lead.
//
// Meta path: renders the resolved campaign / ad set / ad / form chain plus the
// custom answers the lead submitted, pulled from /api/leads/:id/attribution.
//
// Google path: renders the click IDs (gclid/gbraid/wbraid) and UTM params that
// Google Ads stamps on intake, read straight off the lead doc. A lead counts as
// Google-sourced when it has a gclid, a utm_source containing 'google', or a
// leadVendorName starting with "CIVIX Google" (the vendor names stamped by
// leadVendorService). Leads with none of those fall through to null.
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Target, Copy, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet } from '../lib/api.js';

// customFields serializes from a Mongoose Map to a plain object over the wire,
// but read defensively in case a caller hands us the live Map.
function cfGet(cf, key) {
  if (!cf) return undefined;
  if (typeof cf.get === 'function') return cf.get(key);
  return cf[key];
}

function readGoogle(lead) {
  const g = lead.googleAds || {};
  const cf = lead.customFields || {};
  const clickIds = cfGet(cf, 'clickIds') || {};
  const attr = lead.attribution || {};
  const gclid = g.gclid || cfGet(cf, 'gclid') || clickIds.gclid || '';
  return {
    gclid,
    gbraid: g.gbraid || cfGet(cf, 'gbraid') || clickIds.gbraid || '',
    wbraid: g.wbraid || cfGet(cf, 'wbraid') || clickIds.wbraid || '',
    utmSource: attr.utmSource || '',
    utmMedium: attr.utmMedium || '',
    utmCampaign: attr.utmCampaign || '',
    utmContent: attr.utmContent || '',
    utmTerm: attr.utmTerm || '',
    campaignId: attr.adCampaignId || '',
    landingPage: clickIds.landingPage || cfGet(cf, 'landingPage') || attr.landingPageUrl || '',
    vendorName: lead.leadVendorName || '',
  };
}

function isGoogleLead(lead, gg) {
  if (!lead) return false;
  if (gg.gclid || gg.gbraid || gg.wbraid) return true;
  if (gg.utmSource && gg.utmSource.toLowerCase().includes('google')) return true;
  if (gg.vendorName && gg.vendorName.startsWith('CIVIX Google')) return true;
  if (lead.source && lead.source.toLowerCase().includes('google')) return true;
  return false;
}

function truncId(v) {
  if (!v) return '';
  if (v.length <= 20) return v;
  return `${v.slice(0, 12)}…${v.slice(-6)}`;
}

export default function SourceAttribution({ lead }) {
  const [open, setOpen] = useState(false);
  const isMeta = lead && lead.source === 'meta_ads';

  const gg = lead ? readGoogle(lead) : {};
  const isGoogle = !isMeta && isGoogleLead(lead, gg);

  const { data } = useQuery({
    queryKey: ['lead-attribution', lead?._id],
    queryFn: () => apiGet(`/leads/${lead._id}/attribution`),
    enabled: !!lead?._id && isMeta && open,
    staleTime: 60_000,
  });

  if (isMeta) {
    return <MetaAttribution lead={lead} data={data} open={open} setOpen={setOpen} />;
  }

  if (isGoogle) {
    return <GoogleAttribution gg={gg} open={open} setOpen={setOpen} />;
  }

  return null;
}

function MetaAttribution({ lead, data, open, setOpen }) {
  const meta = data?.meta || {
    campaignName: lead.meta_campaign_name || '',
    campaignId: lead.meta_campaign_id || '',
    adsetName: lead.meta_adset_name || '',
    adsetId: lead.meta_adset_id || '',
    adName: lead.meta_ad_name || '',
    adId: lead.meta_ad_id || '',
    formName: lead.meta_form_name || '',
    formId: lead.meta_form_id || '',
  };
  const customAnswers = data?.customAnswers || [];
  // Older Meta-sourced leads (created before this PR) have no meta_* fields.
  // Render an explicit empty state instead of a panel of "—" rows so admins
  // can tell "no data captured" from "data is missing because of a bug".
  const hasAnyMeta = !!(meta.campaignName || meta.adsetName || meta.adName ||
    meta.formName || meta.campaignId || meta.adsetId || meta.adId || meta.formId);
  if (!hasAnyMeta && customAnswers.length === 0) {
    return (
      <div className="mt-3 px-3 py-2 border border-civix-purple/20 bg-civix-purple/5 rounded-md text-[11px] text-white/40">
        No attribution data available
      </div>
    );
  }

  return (
    <div className="mt-3 border border-civix-purple/30 bg-civix-purple/5 rounded-md text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-civix-purple/10 rounded-t-md"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <Target className="w-3.5 h-3.5 text-civix-purple" />
        <span className="font-medium">Source Attribution</span>
        <span className="text-white/50">·</span>
        <span className="text-white/60 truncate">
          {meta.formName || 'Meta Lead Ad'}
          {meta.adsetName ? ` · ${meta.adsetName}` : ''}
        </span>
      </button>
      {open && (
        <div className="border-t border-civix-purple/20 p-3 space-y-2">
          <Row label="Source" value="Meta Ads" />
          {meta.campaignName && <Row label="Campaign" value={meta.campaignName} subtext={meta.campaignId} />}
          {meta.adsetName && <Row label="Ad Set" value={meta.adsetName} subtext={meta.adsetId} />}
          {meta.adName && <Row label="Ad" value={meta.adName} subtext={meta.adId} />}
          {meta.formName && <Row label="Form" value={meta.formName} subtext={meta.formId} />}
          {customAnswers.length > 0 && (
            <>
              <div className="border-t border-civix-purple/20 my-2" />
              <div className="text-[10px] uppercase tracking-wider text-white/40">Custom Answers</div>
              {customAnswers.map((c) => (
                <Row key={c.key} label={c.label} value={String(Array.isArray(c.value) ? c.value.join(', ') : c.value)} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function GoogleAttribution({ gg, open, setOpen }) {
  const hasClickData = !!(gg.gclid || gg.gbraid || gg.wbraid);
  const hasUtms = !!(gg.utmSource || gg.utmMedium || gg.utmCampaign || gg.utmContent || gg.utmTerm);

  // Vendor-name-only leads (no click IDs, no UTMs): a Google source was
  // detected purely from the stamped vendor name, so there's nothing to
  // expand into. Show a minimal badge + note instead of an empty panel.
  if (!hasClickData && !hasUtms) {
    return (
      <div className="mt-3 px-3 py-2 border border-civix-purple/30 bg-civix-purple/5 rounded-md text-xs flex items-center gap-2">
        <Target className="w-3.5 h-3.5 text-civix-purple" />
        <span className="font-medium">Google source</span>
        {gg.vendorName && (
          <>
            <span className="text-white/50">·</span>
            <span className="text-white/70 truncate">{gg.vendorName}</span>
          </>
        )}
        <span className="text-white/40 text-[11px]">— click data missing</span>
      </div>
    );
  }

  const adsUrl = gg.campaignId
    ? `https://ads.google.com/aw/campaigns?ocid=&campaignId=${encodeURIComponent(gg.campaignId)}`
    : 'https://ads.google.com/aw/campaigns';

  return (
    <div className="mt-3 border border-civix-purple/30 bg-civix-purple/5 rounded-md text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-civix-purple/10 rounded-t-md"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <Target className="w-3.5 h-3.5 text-civix-purple" />
        <span className="font-medium">Source Attribution</span>
        <span className="text-white/50">·</span>
        <span className="text-white/60 truncate">
          Google Ads
          {gg.utmCampaign ? ` · ${gg.utmCampaign}` : ''}
        </span>
      </button>
      {open && (
        <div className="border-t border-civix-purple/20 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-civix-purple/20 text-civix-purple font-medium text-[11px]">
              Google Ads
            </span>
            {gg.vendorName && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-white/10 text-white/70 text-[11px] truncate">
                {gg.vendorName}
              </span>
            )}
          </div>

          {gg.gclid && <CopyRow label="gclid" value={gg.gclid} />}
          {gg.gbraid && <CopyRow label="gbraid" value={gg.gbraid} />}
          {gg.wbraid && <CopyRow label="wbraid" value={gg.wbraid} />}

          {hasUtms && (
            <>
              <div className="border-t border-civix-purple/20 my-2" />
              {gg.utmSource && <Row label="utm_source" value={gg.utmSource} />}
              {gg.utmMedium && <Row label="utm_medium" value={gg.utmMedium} />}
              {gg.utmCampaign && <Row label="utm_campaign" value={gg.utmCampaign} subtext={gg.campaignId} />}
              {gg.utmContent && <Row label="utm_content" value={gg.utmContent} />}
              {gg.utmTerm && <Row label="utm_term" value={gg.utmTerm} />}
            </>
          )}

          {gg.landingPage && (
            <>
              <div className="border-t border-civix-purple/20 my-2" />
              <div className="flex items-baseline gap-2">
                <div className="w-24 shrink-0 text-white/50">Landing page</div>
                <a
                  href={gg.landingPage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-0 text-civix-purple hover:underline truncate inline-flex items-center gap-1"
                >
                  <span className="truncate">{gg.landingPage}</span>
                  <ExternalLink className="w-3 h-3 shrink-0 opacity-60" />
                </a>
              </div>
            </>
          )}

          {(gg.utmCampaign || gg.campaignId) && (
            <>
              <div className="border-t border-civix-purple/20 my-2" />
              <a
                href={adsUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={gg.campaignId ? `Campaign ID ${gg.campaignId}` : `Campaign "${gg.utmCampaign}"`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-civix-purple/20 text-civix-purple hover:bg-civix-purple/30 transition text-[11px] font-medium"
              >
                View in Google Ads
                <ExternalLink className="w-3 h-3" />
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CopyRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-2">
      <div className="w-24 shrink-0 text-white/50">{label}</div>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard?.writeText(value);
          toast.success(`${label} copied`);
        }}
        title={value}
        className="flex-1 min-w-0 font-mono text-white/90 inline-flex items-center gap-1.5 hover:text-civix-purple transition"
      >
        <span className="truncate">{truncId(value)}</span>
        <Copy className="w-3 h-3 shrink-0 opacity-60" />
      </button>
    </div>
  );
}

function Row({ label, value, subtext }) {
  return (
    <div className="flex items-baseline gap-2">
      <div className="w-24 shrink-0 text-white/50">{label}</div>
      <div className="flex-1 min-w-0">
        <div className="text-white/90 truncate">{value || '—'}</div>
        {subtext && <div className="text-[10px] text-white/30 truncate">{subtext}</div>}
      </div>
    </div>
  );
}
